import React, { useState, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
interface FilterOption {
  id: string;
  label: string;
}
interface FilterGroup {
  id: string;
  label: string;
  options: FilterOption[];
}
interface SearchFilterProps {
  placeholder?: string;
  filterGroups?: FilterGroup[];
  onSearch: (value: string) => void;
  onFilter: (filters: Record<string, string[]>) => void;
  initialValue?: string;
}
export const SearchFilter: React.FC<SearchFilterProps> = ({
  placeholder = 'Search...',
  filterGroups = [],
  onSearch,
  onFilter,
  initialValue = ''
}) => {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch(e.target.value);
  };
  const toggleFilter = (groupId: string, optionId: string) => {
    setSelectedFilters(prev => {
      const groupFilters = [...(prev[groupId] || [])];
      const optionIndex = groupFilters.indexOf(optionId);
      if (optionIndex >= 0) {
        groupFilters.splice(optionIndex, 1);
      } else {
        groupFilters.push(optionId);
      }
      return {
        ...prev,
        [groupId]: groupFilters
      };
    });
  };
  const clearFilters = () => {
    setSelectedFilters({});
  };
  const hasActiveFilters = Object.values(selectedFilters).some(group => group.length > 0);

  // Update search value when initialValue changes
  useEffect(() => {
    setSearchValue(initialValue);
    onSearch(initialValue);
  }, [initialValue, onSearch]);

  // Call onFilter callback when selectedFilters changes
  useEffect(() => {
    onFilter(selectedFilters);
  }, [selectedFilters, onFilter]);
  return <div className="mb-6">
      <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input type="text" value={searchValue} onChange={handleSearchChange} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" placeholder={placeholder} />
        </div>
        {/* Filter Button */}
        {filterGroups.length > 0 && <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary ${hasActiveFilters ? 'bg-alps-primary text-white border-alps-primary hover:bg-alps-primary-dark' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && <span className="ml-2 bg-alps-primary-dark text-white text-xs rounded-full px-2 py-0.5">
                {Object.values(selectedFilters).reduce((count, group) => count + group.length, 0)}
              </span>}
          </button>}
        {/* Clear Filters Button */}
        {hasActiveFilters && <button onClick={clearFilters} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary">
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </button>}
      </div>
      {/* Filter Panel */}
      {showFilters && filterGroups.length > 0 && <div className="mt-3 p-4 bg-white border border-gray-200 rounded-md shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterGroups.map(group => <div key={group.id} className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">
                  {group.label}
                </h3>
                <div className="space-y-1">
                  {group.options.map(option => <div key={option.id} className="flex items-center">
                      <input id={`filter-${group.id}-${option.id}`} type="checkbox" checked={(selectedFilters[group.id] || []).includes(option.id)} onChange={() => toggleFilter(group.id, option.id)} className="h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded" />
                      <label htmlFor={`filter-${group.id}-${option.id}`} className="ml-2 text-sm text-gray-600">
                        {option.label}
                      </label>
                    </div>)}
                </div>
              </div>)}
          </div>
        </div>}
    </div>;
};